import { useState, useMemo, useEffect, useRef } from 'react';
import { Segment } from '@/data/mockData';

export const MIN_PRICE_EUR_HT = 450;
export const CPM_EUR_HT = 18;
export const MIN_VOLUME_THRESHOLD = Math.ceil((MIN_PRICE_EUR_HT / CPM_EUR_HT) * 1000);

interface UseVolumeEstimationParams {
  selectedSegmentDetails: Segment[];
  singleSelectedSegment: Segment | null;
  isSingleSegmentInCategory: boolean;
  duration: '30' | '60' | '90';
  requestedVolume: string;
  contactType: string;
  requestName: string;
  selectedSegments: string[];
  cpmPrice?: number;  // CPM du client (converti depuis string)
  minimumPrice?: number;  // Prix plancher du client
  apiTotals?: {
    email: number;
    mobile: number;
    emailOrMobile: number;
    emailAndMobile: number;
  } | null;  // Totaux de l'API depuis la catégorie "All" (prend en compte les intersections)
}

interface UseVolumeEstimationReturn {
  estimatedTotals: {
    email: number;
    mobile: number;
    emailOrMobile: number;
    emailAndMobile: number;
  };
  totalPrice: number;
  validateVolume: { isValid: boolean; error: string };
  areButtonsEnabled: boolean;
  estimateCalculated: boolean;
  handleEstimate: () => void;
  /** Invalide l'estimation courante (repasse estimateCalculated à false). */
  resetEstimate: () => void;
}

export const useVolumeEstimation = ({
  selectedSegmentDetails,
  singleSelectedSegment,
  isSingleSegmentInCategory,
  duration,
  requestedVolume,
  contactType,
  requestName,
  selectedSegments,
  cpmPrice,
  minimumPrice,
  apiTotals,
}: UseVolumeEstimationParams): UseVolumeEstimationReturn => {
  const [estimateCalculated, setEstimateCalculated] = useState(false);
  const prevSelectedSegmentDetailsRef = useRef<Segment[]>([]);

  // Reset estimation when selection or duration changes, but only if segments actually changed
  useEffect(() => {
    // Comparer les IDs des segments pour éviter les réinitialisations inutiles
    const currentIds = selectedSegmentDetails.map(s => s.id).sort().join(',');
    const prevIds = prevSelectedSegmentDetailsRef.current.map(s => s.id).sort().join(',');
    
    // Ne réinitialiser que si les segments ont vraiment changé (IDs différents)
    // Si les IDs sont identiques mais que les segments ont maintenant des volumes,
    // c'est probablement que les volumes viennent d'être injectés depuis l'API, donc on ne réinitialise pas
    if (currentIds !== prevIds) {
    setEstimateCalculated(false);
    }
    
    prevSelectedSegmentDetailsRef.current = selectedSegmentDetails;
  }, [selectedSegmentDetails, duration]);

  const estimatedTotals = useMemo(() => {
    const email = selectedSegmentDetails.reduce((sum, segment) => {
      const volumeData = segment.volumes?.[duration];
      return sum + (volumeData?.email || 0);
    }, 0);
    const mobile = selectedSegmentDetails.reduce((sum, segment) => {
      const volumeData = segment.volumes?.[duration];
      return sum + (volumeData?.mobile || 0);
    }, 0);
    const emailOrMobile = selectedSegmentDetails.reduce((sum, segment) => {
      const volumeData = segment.volumes?.[duration];
      return sum + (volumeData?.emailOrMobile || 0);
    }, 0);
    const emailAndMobile = selectedSegmentDetails.reduce((sum, segment) => {
      const volumeData = segment.volumes?.[duration];
      return sum + (volumeData?.emailAndMobile || 0);
    }, 0);
    return { email, mobile, emailOrMobile, emailAndMobile };
  }, [selectedSegmentDetails, duration]);

  const totalPrice = useMemo(() => {
    const volume = parseInt(requestedVolume.replace(/\s/g, '')) || 0;
    if (volume === 0) return 0;
    // Utiliser les valeurs du client si disponibles, sinon les valeurs par défaut
    const cpm = cpmPrice ?? CPM_EUR_HT;
    const minPrice = minimumPrice ?? MIN_PRICE_EUR_HT;
    const calculated = (volume / 1000) * cpm;
    return Math.max(calculated, minPrice);
  }, [requestedVolume, cpmPrice, minimumPrice]);

  const validateVolume = useMemo(() => {
    if (!requestedVolume || !contactType) {
      return { isValid: true, error: '' };
    }

    const requested = parseInt(requestedVolume.replace(/\s/g, ''));
    if (isNaN(requested)) {
      return { isValid: true, error: '' };
    }

    // Single segment validation
    if (singleSelectedSegment) {
      const volumeData = singleSelectedSegment.volumes?.[duration];
      if (!volumeData) {
        return {
          isValid: false,
          error: `Ce segment n'a pas de données disponibles pour une durée de ${duration} jours`,
        };
      }
      
      let maxAvailable = 0;
      if (contactType === 'email') {
        maxAvailable = volumeData.email;
      } else if (contactType === 'mobile') {
        maxAvailable = volumeData.mobile;
      } else if (contactType === 'email_or_mobile') {
        maxAvailable = volumeData.emailOrMobile;
      } else if (contactType === 'email_and_mobile') {
        maxAvailable = volumeData.emailAndMobile;
      }

      if (requested > maxAvailable) {
        return {
          isValid: false,
          error: `Le volume demandé dépasse le maximum disponible (${maxAvailable.toLocaleString('fr-FR')})`,
        };
      }
    }

    // Multiple segments validation (only if estimation was calculated)
    if (!isSingleSegmentInCategory && estimateCalculated) {
      // Utiliser les totaux de l'API si disponibles (ils prennent en compte les intersections),
      // sinon utiliser les totaux calculés (somme simple)
      const totalsToUse = apiTotals || estimatedTotals;
      
      let maxAvailable = 0;
      if (contactType === 'email') {
        maxAvailable = totalsToUse.email;
      } else if (contactType === 'mobile') {
        maxAvailable = totalsToUse.mobile;
      } else if (contactType === 'email_or_mobile') {
        maxAvailable = totalsToUse.emailOrMobile;
      } else if (contactType === 'email_and_mobile') {
        maxAvailable = totalsToUse.emailAndMobile;
      }

      if (requested > maxAvailable) {
        return {
          isValid: false,
          error: `Le volume demandé dépasse le totale disponible (${maxAvailable.toLocaleString('fr-FR')})`,
        };
      }
    }

    return { isValid: true, error: '' };
  }, [singleSelectedSegment, requestedVolume, contactType, duration, isSingleSegmentInCategory, estimateCalculated, estimatedTotals, apiTotals]);

  const areButtonsEnabled = useMemo(() => {
    if (!selectedSegments.length || !requestedVolume || !contactType || !requestName) {
      return false;
    }

    // Valider que le volume demandé est strictement supérieur à 0
    const requestedVolumeNumber = parseInt(requestedVolume.replace(/\s/g, ''), 10) || 0;
    if (requestedVolumeNumber <= 0) {
      return false;
    }

    // Single segment: enable immediately if volume is valid
    if (isSingleSegmentInCategory) {
      return validateVolume.isValid;
    }

    // Multiple segments: require estimation first
    return estimateCalculated && validateVolume.isValid;
  }, [selectedSegments, requestedVolume, contactType, requestName, isSingleSegmentInCategory, validateVolume, estimateCalculated]);

  const handleEstimate = () => {
    setEstimateCalculated(true);
  };

  const resetEstimate = () => {
    setEstimateCalculated(false);
  };

  return {
    estimatedTotals,
    totalPrice,
    validateVolume,
    areButtonsEnabled,
    estimateCalculated,
    handleEstimate,
    resetEstimate,
  };
};

